import { partsOf } from './civil.js'
import type { CivilDate } from './civil.js'

/**
 * Mês de referência: `string` no formato `YYYY-MM`. Usado em
 * `Invoice.referenceMonth`, nos filtros de relatório e nas abas de mês da tela
 * de Transações. Mesmo raciocínio da data civil: string, ordenável, sem
 * ambiguidade. Ver `Docs/03-Arquitetura/Dinheiro, datas e fuso.md`.
 */
export type ReferenceMonth = string

const SHAPE = /^\d{4}-(0[1-9]|1[0-2])$/

const MIN_YEAR = 0
const MAX_YEAR = 9999

/**
 * A guarda de fronteira do mês, irmã de `isCivilDate`.
 *
 * Nasceu privada — a decisão 63 diz que a superfície aparece quando há quem
 * use, e até a Task 8 quem cobrava a forma para fora era `monthStart`, por
 * chamar o `assert` abaixo. O consumidor apareceu na Task 11: o mês vive na
 * **URL** (`/transacoes?mes=2026-07`, Docs/04-Design/Navegação.md), e o que vem
 * da URL é `unknown` de verdade — digitado, colado, ou de um link antigo. A
 * rota precisa **decidir** sem lançar, porque um mês inválido na barra de
 * endereço não pode derrubar a tela: ela cai no mês corrente.
 *
 * Reprova o mês que não existe (`'2026-13'`) e não só a forma, pelo mesmo
 * motivo de `isCivilDate` reprovar `'2026-02-30'`.
 */
export function isReferenceMonth(v: unknown): v is ReferenceMonth {
  return typeof v === 'string' && SHAPE.test(v)
}

function assertReferenceMonth(v: unknown): asserts v is ReferenceMonth {
  if (!isReferenceMonth(v)) {
    throw new Error(`mês de referência: esperava 'YYYY-MM', recebi ${JSON.stringify(v)}`)
  }
}

/** `'2026-07-15'` → `'2026-07'`. O dia não entra na conta. */
export function referenceMonthOf(date: CivilDate): ReferenceMonth {
  // `partsOf` valida o calendário, então '2026-02-30' não vira '2026-02'.
  const { year, month } = partsOf(date)
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}`
}

/**
 * Anda `delta` meses. Aceita negativo.
 *
 * Conta em meses absolutos desde o ano 0 e volta, em vez de somar ao mês e
 * corrigir o estouro à mão: a correção à mão é onde nascem os erros de dezembro
 * virando mês 0, e os dois sentidos precisam funcionar.
 */
export function addMonths(month: ReferenceMonth, delta: number): ReferenceMonth {
  assertReferenceMonth(month)
  if (!Number.isSafeInteger(delta)) {
    throw new Error(`addMonths: meses precisa ser inteiro, recebi ${String(delta)}`)
  }
  const year = Number(month.slice(0, 4))
  const index = Number(month.slice(5, 7)) - 1

  const absolute = year * 12 + index + delta
  const movedYear = Math.floor(absolute / 12)
  const movedMonth = absolute - movedYear * 12 + 1

  if (movedYear < MIN_YEAR || movedYear > MAX_YEAR) {
    throw new Error(
      `addMonths: ano precisa ficar entre ${MIN_YEAR} e ${MAX_YEAR}, cheguei em ${String(movedYear)}`,
    )
  }
  return `${String(movedYear).padStart(4, '0')}-${String(movedMonth).padStart(2, '0')}`
}

/**
 * O primeiro dia do mês, como data civil: `'2026-07'` → `'2026-07-01'`.
 *
 * Existe para recortar um mês numa faixa de `CivilDate` — `>= monthStart(mes)`
 * e `< monthStart(addMonths(mes, 1))` — que é como a consulta do Firestore
 * pergunta "as transações deste mês?" sobre um campo `YYYY-MM-DD`. A faixa é
 * **fechada embaixo e aberta em cima** de propósito: o limite de cima sai do
 * mesmo `addMonths` que já existe, e assim nenhum lugar precisa saber quantos
 * dias tem o mês. Fevereiro, ano bissexto e dezembro caem todos na mesma
 * expressão.
 *
 * O dia 1 existe em todo mês, então não há saturação a considerar aqui — o que
 * `civilFromParts` faria por 29, 30 e 31 não tem como acontecer com 01.
 */
export function monthStart(month: ReferenceMonth): CivilDate {
  assertReferenceMonth(month)
  return `${month}-01`
}
